import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import api from "../api";
import { Modal } from "../components/ui";
import DateIT from "../components/DateIT";
import EmailPreview from "../components/EmailPreview";
import { ContactSelect, linkedToBody } from "../components/TaskMove";

function isOverdue(t) {
  return t.status !== "fatto" && t.due_date && new Date(t.due_date) < new Date(new Date().toDateString());
}

export default function TasksPage() {
  const [filter, setFilter] = useState("da_fare");
  const [list, setList] = useState([]);
  const [edit, setEdit] = useState(null); // {id, title, description, due, status}
  const [nuovo, setNuovo] = useState(null); // {title, description, due, company_id}
  const [companies, setCompanies] = useState([]);
  const [persons, setPersons] = useState([]);

  useEffect(() => {
    api.get("/companies?limit=2000").then((r) => setCompanies(r.data)).catch(() => {});
    api.get("/persons").then((r) => setPersons(r.data)).catch(() => {});
  }, []);

  const cname = Object.fromEntries(companies.map((c) => [c.id, c.name]));
  const pname = Object.fromEntries(persons.map((p) => [p.id, `${p.last_name || ""} ${p.first_name || ""}`.trim()]));

  const load = useCallback(() => {
    const q = filter === "tutti" ? "" : `?status=${filter}`;
    api.get(`/tasks${q}`).then((r) => setList(r.data)).catch(() => {});
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const toggle = async (t) => {
    if (t.status !== "fatto" && t.cc_kind === "chiamare") {
      const v = window.prompt("Esito della chiamata?\n1 = ha risposto\n2 = nessuna risposta\n3 = da richiamare senza data\n\n(2 e 3 avviano il follow-up automatico B)", "1");
      if (v === null) return;
      const esito = { "1": "risposto", "2": "nessuna_risposta", "3": "da_richiamare" }[v.trim()];
      if (!esito) { alert("Scegli 1, 2 o 3"); return; }
      await api.put(`/tasks/${t.id}`, { status: "fatto", esito });
      if (esito !== "risposto") alert("Incarico chiuso: sequenza follow-up B avviata (B1 a +30gg dalla email 06).");
      load();
      return;
    }
    await api.put(`/tasks/${t.id}`, { status: t.status === "fatto" ? "da_fare" : "fatto" });
    load();
  };
  const remove = async (id) => { if (window.confirm("Eliminare l'incarico?")) { await api.delete(`/tasks/${id}`); load(); } };
  const openEdit = (t) => setEdit({ id: t.id, title: t.title || "", description: t.description || "", due: t.due_date ? String(t.due_date).slice(0, 10) : "", status: t.status || "da_fare", email_id: t.email_id || null, linked: t.company_id ? `c:${t.company_id}` : t.person_id ? `p:${t.person_id}` : "" });
  const saveEdit = async () => {
    if (!edit.title.trim()) { alert("La descrizione è obbligatoria"); return; }
    try {
      await api.put(`/tasks/${edit.id}`, { title: edit.title.trim(), description: edit.description.trim() || null, due_date: edit.due || null, status: edit.status, ...linkedToBody(edit.linked) });
      setEdit(null); load();
    } catch (e) { alert("Errore: " + (e?.response?.data?.detail || e.message)); }
  };

  const saveNuovo = async () => {
    if (!nuovo.title.trim()) { alert("La descrizione è obbligatoria"); return; }
    try {
      await api.post("/tasks", {
        title: nuovo.title.trim(), description: nuovo.description.trim() || null,
        due_date: nuovo.due || null, ...linkedToBody(nuovo.linked),
      });
      setNuovo(null); setFilter("da_fare"); load();
    } catch (e) { alert("Errore: " + (e?.response?.data?.detail || e.message)); }
  };

  return (
    <div>
      <h1 className="page-title">Incarichi</h1>
      <p className="page-sub">Cose da fare, con scadenza. Spesso nascono da una mail. Clicca su un incarico per modificarlo.</p>

      <div className="tabs" style={{ display: "flex", alignItems: "center" }}>
        <button className={filter === "da_fare" ? "active" : ""} onClick={() => setFilter("da_fare")}>Da fare</button>
        <button className={filter === "fatto" ? "active" : ""} onClick={() => setFilter("fatto")}>Fatti</button>
        <button className={filter === "tutti" ? "active" : ""} onClick={() => setFilter("tutti")}>Tutti</button>
        <span style={{ marginLeft: "auto" }}>
          <button className="btn primary" onClick={() => setNuovo({ title: "", description: "", due: "", linked: "" })} data-testid="task-new-btn">➕ Nuovo incarico</button>
        </span>
      </div>

      <table>
        <thead><tr><th></th><th>Incarico</th><th>Scadenza</th><th>Collegato a</th><th>Origine</th><th></th></tr></thead>
        <tbody>
          {list.map((t) => (
            <tr key={t.id} data-testid={`task-row-${t.id}`}>
              <td><input type="checkbox" style={{ width: "auto" }} checked={t.status === "fatto"} onChange={() => toggle(t)} data-testid={`task-toggle-${t.id}`} /></td>
              <td>
                <button className="link-btn" onClick={() => openEdit(t)} data-testid={`task-open-${t.id}`}
                  style={{ background: "none", border: "none", color: "var(--ink)", cursor: "pointer", textAlign: "left", padding: 0, font: "inherit", textDecoration: t.status === "fatto" ? "line-through" : "underline dotted" }}>
                  {t.title}
                </button>
                {t.description && <div className="muted" style={{ fontSize: 12, whiteSpace: "pre-wrap" }}>{t.description}</div>}
              </td>
              <td className={isOverdue(t) ? "task-overdue" : ""}>{t.due_date ? new Date(t.due_date).toLocaleDateString("it-IT") : "—"}{isOverdue(t) ? " (scaduto)" : ""}</td>
              <td data-testid={`task-linked-${t.id}`}>
                {t.company_id ? <Link to={`/aziende/${t.company_id}`}>{cname[t.company_id] || "azienda"}</Link>
                  : t.person_id ? <Link to={`/persone/${t.person_id}`}>{pname[t.person_id] || "persona"}</Link>
                  : <span className="muted">—</span>}
              </td>
              <td>{t.email_id ? <Link to={`/email/${t.email_id}`}>email</Link> : "—"}</td>
              <td style={{ whiteSpace: "nowrap" }}>
                <button className="btn sm" onClick={() => openEdit(t)} data-testid={`task-edit-${t.id}`}>Modifica</button>{" "}
                <button className="btn sm danger" onClick={() => remove(t.id)}>Elimina</button>
              </td>
            </tr>
          ))}
          {list.length === 0 && <tr><td colSpan={6}><div className="empty">Nessun incarico.</div></td></tr>}
        </tbody>
      </table>

      {nuovo && (
        <Modal title="Nuovo incarico" onClose={() => setNuovo(null)}>
          <div className="field"><label>Descrizione *</label><input value={nuovo.title} onChange={(e) => setNuovo({ ...nuovo, title: e.target.value })} placeholder="Es. Preparare dichiarazione IVA" data-testid="task-new-title" /></div>
          <div className="field"><label>Note</label><textarea rows={3} value={nuovo.description} onChange={(e) => setNuovo({ ...nuovo, description: e.target.value })} data-testid="task-new-desc" /></div>
          <div className="grid-2">
            <div className="field"><label>Scadenza</label><DateIT value={nuovo.due} onChange={(v) => setNuovo({ ...nuovo, due: v })} data-testid="task-new-due" /></div>
            <div className="field"><label>Collegato a (cliente, lead o persona)</label>
              <ContactSelect value={nuovo.linked} onChange={(v) => setNuovo({ ...nuovo, linked: v })} companies={companies} persons={persons} testid="task-new-linked" />
            </div>
          </div>
          <div className="modal-actions">
            <button className="btn" onClick={() => setNuovo(null)}>Annulla</button>
            <button className="btn primary" onClick={saveNuovo} data-testid="task-new-save">Crea incarico</button>
          </div>
        </Modal>
      )}

      {edit && (
        <Modal title="Modifica incarico" onClose={() => setEdit(null)} wide={!!edit.email_id}>
          <div className="field"><label>Descrizione *</label><input value={edit.title} onChange={(e) => setEdit({ ...edit, title: e.target.value })} data-testid="task-edit-title" /></div>
          <div className="field"><label>Note</label><textarea rows={3} value={edit.description} onChange={(e) => setEdit({ ...edit, description: e.target.value })} data-testid="task-edit-desc" /></div>
          <div className="grid-2">
            <div className="field"><label>Scadenza</label><DateIT value={edit.due} onChange={(v) => setEdit({ ...edit, due: v })} data-testid="task-edit-due" /></div>
            <div className="field"><label>Stato</label>
              <select value={edit.status} onChange={(e) => setEdit({ ...edit, status: e.target.value })} data-testid="task-edit-status">
                <option value="da_fare">Da fare</option>
                <option value="fatto">Fatto</option>
              </select>
            </div>
          </div>
          <div className="field"><label>Collegato a (cliente, lead o persona)</label>
            <ContactSelect value={edit.linked} onChange={(v) => setEdit({ ...edit, linked: v })} companies={companies} persons={persons} testid="task-edit-linked" />
          </div>
          {edit.email_id && (
            <div className="field">
              <label>Mail collegata</label>
              <EmailPreview emailId={edit.email_id} />
            </div>
          )}
          <div className="modal-actions">
            <button className="btn" onClick={() => setEdit(null)}>Annulla</button>
            <button className="btn primary" onClick={saveEdit} data-testid="task-edit-save">Salva</button>
          </div>
        </Modal>
      )}
    </div>
  );
}
